import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, MoreVertical, AlertTriangle, Info, AlertCircle, Inbox } from 'lucide-react';
import { Tooltip } from '../Tooltip';
import './TableMatrix.css';

// ── Tipos ────────────────────────────────────────────────────────────────────

export type MatrixDensity = 'comfortable' | 'intermediate' | 'compact';

export interface MatrixField<T> {
  key: string;
  header: React.ReactNode;
  /** 'toggle' = booleano por fila; 'exclusive' = un único seleccionado por contexto. */
  type: 'toggle' | 'exclusive';
  value: (entity: T) => boolean;
  onChange: (entity: T, next: boolean) => void;
  disabled?: (entity: T) => boolean;
  locked?: (entity: T) => boolean;
  lockedTooltip?: string;
  ariaLabel?: (entity: T) => string;
}

export interface MatrixContext<T> {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
  fields: MatrixField<T>[];
}

export interface MatrixLeadColumn<T> {
  header?: React.ReactNode;
  badge?: (entity: T) => React.ReactNode;
  label: (entity: T) => React.ReactNode;
  width?: number | string;
}

export interface MatrixStateColumn<T> {
  header?: React.ReactNode;
  value: (entity: T) => boolean;
  onChange: (entity: T, next: boolean) => void;
  disabled?: (entity: T) => boolean;
  ariaLabel?: (entity: T) => string;
}

export interface MatrixAction<T> {
  icon: React.ReactNode | ((entity: T) => React.ReactNode);
  label: string;
  onClick: (entity: T) => void;
  variant?: 'default' | 'destructive';
  visible?: (entity: T) => boolean;
}

export interface MatrixNotice {
  tone?: 'info' | 'warning' | 'error';
  content: React.ReactNode;
}

export interface TableMatrixProps<T> {
  entities: T[];
  keyExtractor: (entity: T) => string | number;
  lead: MatrixLeadColumn<T>;
  state?: MatrixStateColumn<T>;
  contexts: MatrixContext<T>[];
  actions?: MatrixAction<T>[];
  menuActions?: MatrixAction<T>[];
  actionsHeader?: React.ReactNode;
  rowDisabled?: (entity: T) => boolean;
  notices?: MatrixNotice[];
  footnote?: React.ReactNode;
  density?: MatrixDensity;
  stickyHeader?: boolean;
  minTableWidth?: number;
  loading?: boolean;
  loadingText?: string;
  emptyState?: React.ReactNode;
  className?: string;
}

// ── Utilidades internas ───────────────────────────────────────────────────────

const noticeIcon = (tone: MatrixNotice['tone']) => {
  if (tone === 'error') return <AlertCircle className="w-4 h-4" />;
  if (tone === 'info') return <Info className="w-4 h-4" />;
  return <AlertTriangle className="w-4 h-4" />;
};

// ── Componente ────────────────────────────────────────────────────────────────

export const TableMatrix = <T,>({
  entities,
  keyExtractor,
  lead,
  state,
  contexts,
  actions = [],
  menuActions = [],
  actionsHeader = 'Acciones',
  rowDisabled,
  notices = [],
  footnote,
  density = 'comfortable',
  stickyHeader = true,
  minTableWidth = 760,
  loading = false,
  loadingText = 'Cargando...',
  emptyState,
  className = '',
}: TableMatrixProps<T>) => {
  const [menuKey, setMenuKey] = useState<string | number | null>(null);
  const [menuCoords, setMenuCoords] = useState<{ top: number; left: number } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  const cellPy = density === 'compact' ? 'py-1' : density === 'intermediate' ? 'py-1.5' : 'py-2.5';
  const hasActions = actions.length > 0 || menuActions.length > 0;

  const isRowOff = useCallback(
    (entity: T) => (rowDisabled ? rowDisabled(entity) : state ? !state.value(entity) : false),
    [rowDisabled, state],
  );

  const closeMenu = useCallback(() => {
    setMenuKey(null);
    setMenuCoords(null);
  }, []);

  useEffect(() => {
    if (menuKey === null) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && menuRef.current.contains(e.target as Node)) return;
      closeMenu();
    };
    const onScroll = () => closeMenu();
    document.addEventListener('mousedown', onDown);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [menuKey, closeMenu]);

  const openMenu = (entity: T, e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    const key = keyExtractor(entity);
    if (menuKey === key) return closeMenu();
    const rect = e.currentTarget.getBoundingClientRect();
    const W = 176;
    const left = Math.max(8 + window.scrollX, window.scrollX + rect.right - W);
    setMenuCoords({ top: window.scrollY + rect.bottom + 6, left });
    setMenuKey(key);
  };

  const activeEntity = entities.find((it) => keyExtractor(it) === menuKey);

  const thBase = `px-4 ${cellPy} text-caption-medium font-sans text-tertiary whitespace-nowrap bg-surface-1`;

  const head = (
    <thead className={`border-b border-default ${stickyHeader ? 'sticky top-0 z-20' : ''}`}>
      <tr>
        <th
          rowSpan={2}
          className={`${thBase} text-left align-bottom`}
          style={lead.width ? { width: lead.width, minWidth: lead.width } : undefined}
        >
          {lead.header}
        </th>

        {state && (
          <th rowSpan={2} className={`${thBase} text-center align-bottom`}>
            {state.header ?? 'Estado'}
          </th>
        )}

        {contexts.map((ctx) => (
          <th key={ctx.key} colSpan={ctx.fields.length} className="tm-divcol bg-surface-1 text-center align-bottom px-4 pt-3">
            <span className="tm-grouplabel">
              {ctx.icon}
              {ctx.label}
            </span>
          </th>
        ))}

        {hasActions && (
          <th rowSpan={2} className={`${thBase} tm-divcol text-center align-bottom`}>
            {actionsHeader}
          </th>
        )}
      </tr>
      <tr>
        {contexts.flatMap((ctx) =>
          ctx.fields.map((f, i) => (
            <th key={`${ctx.key}-${f.key}`} className={`${thBase} text-center ${i === 0 ? 'tm-divcol' : ''}`}>
              {f.header}
            </th>
          )),
        )}
      </tr>
    </thead>
  );

  const renderField = (entity: T, ctx: MatrixContext<T>, field: MatrixField<T>, fieldIndex: number) => {
    const cls = `px-4 ${cellPy} text-center align-middle ${fieldIndex === 0 ? 'tm-divcol' : ''}`;
    const key = `${keyExtractor(entity)}-${ctx.key}-${field.key}`;

    if (field.locked?.(entity)) {
      return (
        <td key={key} className={cls}>
          <span className="tm-lock" title={field.lockedTooltip}>
            <Lock size={16} />
          </span>
        </td>
      );
    }

    const disabled = isRowOff(entity) || field.disabled?.(entity) || false;
    const checked = field.value(entity);

    if (field.type === 'exclusive') {
      return (
        <td key={key} className={`${cls} ${checked ? 'tm-cell-selected' : ''}`}>
          <input
            type="radio"
            className="tm-radio"
            name={`${ctx.key}__${field.key}`}
            checked={checked}
            disabled={disabled}
            onChange={() => field.onChange(entity, true)}
            aria-label={field.ariaLabel?.(entity)}
          />
        </td>
      );
    }

    return (
      <td key={key} className={cls}>
        <input
          type="checkbox"
          className="tm-toggle"
          checked={checked}
          disabled={disabled}
          onChange={(e) => field.onChange(entity, e.target.checked)}
          aria-label={field.ariaLabel?.(entity)}
        />
      </td>
    );
  };

  const contextMenu =
    typeof window !== 'undefined' && menuKey !== null && menuCoords && activeEntity
      ? createPortal(
          <div ref={menuRef} className="tm-menu" style={{ position: 'absolute', top: menuCoords.top, left: menuCoords.left }}>
            {menuActions
              .filter((a) => !a.visible || a.visible(activeEntity))
              .map((a) => {
                const icon = typeof a.icon === 'function' ? a.icon(activeEntity) : a.icon;
                return (
                  <button type="button"
                    key={a.label}
                    className={a.variant === 'destructive' ? 'tm-danger' : ''}
                    onClick={() => {
                      a.onClick(activeEntity);
                      closeMenu();
                    }}
                  >
                    {icon && <span className="w-4 h-4 inline-flex items-center justify-center">{icon}</span>}
                    {a.label}
                  </button>
                );
              })}
          </div>,
          document.body,
        )
      : null;

  return (
    <>
      <div className={`tm-card border border-default rounded-lg overflow-hidden bg-surface-0 ${className}`}>
        {notices.map((n) => (
          <div key={`${n.tone ?? 'warning'}-${String(n.content)}`} className={`tm-banner tm-banner-${n.tone ?? 'warning'}`}>
            {noticeIcon(n.tone)}
            <span>{n.content}</span>
          </div>
        ))}

        <div className="overflow-auto relative">
          <table className="tm-table w-full border-collapse text-ui-sm" style={{ minWidth: `${minTableWidth}px` }}>
            {head}
            {!loading && entities.length > 0 && (
              <tbody>
                {entities.map((entity) => {
                  const k = keyExtractor(entity);
                  const off = isRowOff(entity);
                  return (
                    <tr key={k} className={`tm-row border-b border-muted last:border-b-0 ${off ? 'tm-row-disabled' : ''}`}>
                      <td className={`px-4 ${cellPy} text-left align-middle text-ui-sm font-sans text-primary`}>
                        <span className="inline-flex items-center gap-2.5">
                          {lead.badge && <span className="tm-badge">{lead.badge(entity)}</span>}
                          <span className="font-medium">{lead.label(entity)}</span>
                        </span>
                      </td>

                      {state && (
                        <td className={`px-4 ${cellPy} text-center align-middle`}>
                          <input
                            type="checkbox"
                            className="tm-toggle"
                            checked={state.value(entity)}
                            disabled={state.disabled?.(entity) || false}
                            onChange={(e) => state.onChange(entity, e.target.checked)}
                            aria-label={state.ariaLabel?.(entity)}
                          />
                        </td>
                      )}

                      {contexts.flatMap((ctx) => ctx.fields.map((f, i) => renderField(entity, ctx, f, i)))}

                      {hasActions && (
                        <td className={`px-4 ${cellPy} text-center align-middle tm-divcol`}>
                          <div className="inline-flex items-center justify-center gap-1">
                            {actions
                              .filter((a) => !a.visible || a.visible(entity))
                              .map((a) => {
                                const icon = typeof a.icon === 'function' ? a.icon(entity) : a.icon;
                                return (
                                  <Tooltip key={a.label} text={a.label} position="top" noWrap>
                                    <button type="button"
                                      className="tm-actbtn"
                                      aria-label={a.label}
                                      onClick={() => a.onClick(entity)}
                                    >
                                      {icon}
                                    </button>
                                  </Tooltip>
                                );
                              })}
                            {menuActions.length > 0 && (
                              <button type="button"
                                className="tm-actbtn"
                                title="Más opciones"
                                aria-label="Más opciones"
                                onClick={(e) => openMenu(entity, e)}
                              >
                                <MoreVertical className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            )}
          </table>

          {loading && (
            <div className="tm-state">
              <div className="tm-spinner">
                <span style={{ animationDelay: '0s' }} />
                <span style={{ animationDelay: '0.18s' }} />
                <span style={{ animationDelay: '0.36s' }} />
              </div>
              <span className="text-xs text-tertiary">{loadingText}</span>
            </div>
          )}

          {!loading && entities.length === 0 && (
            emptyState ?? (
              <div className="tm-state">
                <Inbox className="w-6 h-6 text-tertiary" />
                <p className="text-body-sm text-tertiary">No hay datos para mostrar</p>
              </div>
            )
          )}
        </div>

        {footnote && <div className="tm-foot">{footnote}</div>}
      </div>

      {contextMenu}
    </>
  );
};

TableMatrix.displayName = 'TableMatrix';



